import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Table,
  Button,
} from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

// Convert a Date to a local yyyy-MM-dd string (avoids UTC off-by-one).
const toDateParam = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const looksLikeDate = (value) =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}(T|\s)/.test(value);

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (looksLikeDate(value)) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )}`;
    }
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// "extra_amount" -> "Extra Amount"
const humanize = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const PaymentRepayment = () => {
  const { cid } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const fetchRepayments = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      // Only send date params when set — empty/null dates can break LocalDate binding,
      // which is why the plain customer_id call (used elsewhere) returns data and this didn't.
      const params = { customer_id: cid };
      const from = toDateParam(fromDate);
      const to = toDateParam(toDate);
      if (from) params.from_date = from;
      if (to) params.to_date = to;

      const response = await axiosInstance.get(URL.PAYMENT_REPAYMENT_LIST, { params });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching payment repayments:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Load once on mount (no date filter), then user can refine by date.
  useEffect(() => {
    fetchRepayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <Card className="bg-secondary shadow">
      <CardHeader className="bg-white border-0">
        <Row className="align-items-center">
          <Col xs="12">
            <h3 className="mb-0">Installment</h3>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        <Row className="align-items-end mb-4">
          <Col xs="12" md="3" className="mb-2">
            <label className="form-control-label d-block">From Date</label>
            <DatePicker
              className="form-control-alternative form-control w-100"
              selected={fromDate}
              onChange={(date) => setFromDate(date)}
              placeholderText="From Date"
              dateFormat="yyyy-MM-dd"
              maxDate={new Date()}
              isClearable
            />
          </Col>
          <Col xs="12" md="3" className="mb-2">
            <label className="form-control-label d-block">To Date</label>
            <DatePicker
              className="form-control-alternative form-control w-100"
              selected={toDate}
              onChange={(date) => setToDate(date)}
              placeholderText="To Date"
              dateFormat="yyyy-MM-dd"
              maxDate={new Date()}
              isClearable
            />
          </Col>
          <Col xs="12" md="3" className="mb-2">
            <Button color="primary" onClick={fetchRepayments} disabled={loading}>
              {loading ? "Loading..." : "Search"}
            </Button>
          </Col>
        </Row>

        <Table
          className="align-items-center table-flush"
          responsive
          bordered
          hover
        >
          <thead className="thead-light">
            <tr>
              <th>#</th>
              {columns.map((col) => (
                <th key={col}>{humanize(col)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row, idx) => (
                <tr key={row.id ?? idx}>
                  <td>{idx + 1}</td>
                  {columns.map((col) => (
                    <td key={col}>{formatCell(row[col])}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + 1 || 2}>
                  {loading ? "Loading..." : "No repayment records found"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

export default PaymentRepayment;
